import { FC, useMemo } from 'react'
import Edito, { EditoProps } from 'src/components/Edito'
import Illustration from 'src/components/Illustration'
import {
  ImageCaption,
  ImageTransformer,
  UrlImageTransformer,
} from 'src/relay/Transformers/ImageTransformer'
import { ButtonTransformer } from 'src/relay/Transformers/ButtonTransormer'
import VideoYoutube from 'src/components/VideoYoutube'
import { RichTextTransformer } from 'src/relay/Transformers/RichTextTransformer'
import { useTranslation } from 'react-i18next'
import prettyBytes from 'pretty-bytes'

import { BlockProps } from '../props'

import { ContentBlockProps } from './props'

const ContentBlock: FC<BlockProps<ContentBlockProps>> = ({
  data,
  configuration,
  locale,
}) => {
  const { t } = useTranslation()

  // Strapi v4 wraps media as `{ data: { attributes: { ... } } }`; v5 returns it
  // flat. Unwrap once so the rest of this block reads a single shape.
  const desktopImage: any =
    (data?.desktopImage as any)?.data ?? data?.desktopImage
  const mobileImage: any = (data?.mobileImage as any)?.data ?? data?.mobileImage
  const pdf: any = (data?.pdf as any)?.data ?? data?.pdf
  const pdfAttrs: any = pdf?.attributes ?? pdf

  const props: EditoProps = useMemo(
    () => ({
      title: data.title || '',
      compact: Boolean(data.compact),
      ...(data.content && {
        richTextProps: RichTextTransformer(data.content, configuration, locale),
      }),
      ...(pdfAttrs?.url && {
        downloadButtons: [
          {
            label: t('edito.downloadButton.text'),
            href: UrlImageTransformer(pdfAttrs.url),
            target: '_blank',
            rel: 'noopener noreferrer',
            details: t('edito.downloadButton.file.size', {
              size: prettyBytes(Math.floor(pdfAttrs?.size || 0) * 1000, {
                locale,
              }),
            }),
          },
        ],
      }),
      actionButtons: data.cta?.url
        ? [ButtonTransformer({ data: data.cta })].filter((button) => !!button)
        : undefined,
      ...(desktopImage && {
        mediaFirstOnDesktop: data?.imagePosition === 'left',
        mediaFirstOnMobile:
          (!data?.mobileImagePosition && data?.imagePosition === 'right') ||
          data?.mobileImagePosition === 'top',
        media: data.videoId ? (
          <VideoYoutube
            options={{ videoId: data.videoId }}
            cover={{
              imageProps: ImageTransformer(desktopImage),
              mobileImageProps: ImageTransformer(mobileImage),
            }}
            texts={{
              play: t('video.play'),
              playVideo: t('video.playVideo'),
              pause: t('video.pause'),
              skip: t('video.skip'),
              mute: t('video.mute'),
              unmute: t('video.unmute'),
              transcript: t('video.transcript'),
              fullscreen: t('video.fullscreen'),
              exitFullscreen: t('video.exitFullscreen'),
            }}
          />
        ) : (
          <Illustration
            caption={ImageCaption(desktopImage) || ImageCaption(mobileImage)}
            pictureProps={{
              imageProps: ImageTransformer(desktopImage, {
                tablet: '100vw, 50vw',
              }),
              mobileImageProps: ImageTransformer(mobileImage, {
                tablet: '100vw, 50vw',
              }),
            }}
          />
        ),
      }),
    }),
    [configuration, data, locale, t, desktopImage, mobileImage, pdfAttrs]
  )

  return <Edito {...props} />
}

export default ContentBlock
